// src/auth/protected-route.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import ImavenLoadingScreen from '../Components/Helpers/loading-screen'
import axios from 'axios';
import { isTeamMemberAuthenticated, getTeamUser } from '../services/teamAuthService';
import GuestDashboardOverlay from './GuestDashboardOverlay';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loginWithRedirect, isLoading, getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const [accessStatus, setAccessStatus] = useState("not_started");
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasTeamMembers, setHasTeamMembers] = useState(false);
  const [checkingTeamMembers, setCheckingTeamMembers] = useState(true);
  
  // Check if user has completed the secondary (member) login
  const isMemberAuthenticated = isTeamMemberAuthenticated();
  const memberUser = getTeamUser();

  // Helper function to check if current path is onboarding
  const isOnOnboardingPath = () => {
    return location.pathname === '/settings/onboarding' || location.pathname === '/onboarding';
  };
  
  // Helper function to check if on member login path
  const isOnMemberLoginPath = () => {
    return location.pathname === '/member-login';
  };

  // NOTE: We no longer auto-redirect to Auth0 login
  // Instead, we show the dashboard with a guest overlay prompting sign up/login
  // This prevents automatic user creation in the database for visitors

  // Check if company has team members (after Auth0 login)
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const checkTeamMembers = async () => {
        try {
          const token = await getAccessTokenSilently();
          const response = await axios.get(`${process.env.REACT_APP_SERVER}team/members`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          const members = response.data.members || [];
          setHasTeamMembers(members.length > 0);
          setCheckingTeamMembers(false);
        } catch (error) {
          console.error('Failed to check team members:', error);
          setHasTeamMembers(false);
          setCheckingTeamMembers(false);
        }
      };

      checkTeamMembers();
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  // Add a new effect to check database flag when authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      // Here you should implement the logic to check your DB flag
      const checkDatabaseFlag = async () => {
        // The real onboarding state comes ONLY from a successful response
        // (server returns "not_registered" for a genuinely new user). A failed
        // request (token refresh race, transient 401/5xx, getAccessTokenSilently
        // throwing) must NOT be treated as "not onboarded" — doing that traps an
        // already-approved adviser on the onboarding form. So we retry, then fall
        // to an explicit "error" state (recoverable screen), never to onboarding.
        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            const token = await getAccessTokenSilently();
            const response = await axios.get(`${process.env.REACT_APP_SERVER}onboarding/status`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            // Expecting "not_registered", "not_started", "pending", or "approved"
            setAccessStatus(response.data.onboardingStatus);
            setCheckingAccess(false);
            return;
          } catch (error) {
            console.error(`Failed to check access flag (attempt ${attempt}/${maxAttempts}):`, error);
            if (attempt < maxAttempts) {
              await new Promise(r => setTimeout(r, 800 * attempt)); // backoff, then retry
              continue;
            }
            setAccessStatus("error"); // -> recoverable error screen, NOT onboarding
            setCheckingAccess(false);
          }
        }
      };

      checkDatabaseFlag();
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);
  
  // Redirect to member login if team members exist but user hasn't done secondary login
  useEffect(() => {
    if (isAuthenticated && !isLoading && !checkingTeamMembers && !checkingAccess) {
      if (hasTeamMembers && !isMemberAuthenticated && accessStatus === "approved") {
        // Team members exist but user hasn't logged in as a member yet
        if (!isOnMemberLoginPath()) {
          navigate('/member-login');
        }
      }
    }
  }, [isAuthenticated, isLoading, checkingTeamMembers, checkingAccess, hasTeamMembers, isMemberAuthenticated, accessStatus, navigate]);

  // Handle navigation to onboarding page when status is "not_started" or "not_registered"
  useEffect(() => {
    if (isAuthenticated && !checkingAccess && (accessStatus === "not_started" || accessStatus === "not_registered")) {
      // Check if we're already on the onboarding page to avoid infinite redirect
      if (!isOnOnboardingPath()) {
        navigate('/settings/onboarding');
      }
    }
  }, [isAuthenticated, checkingAccess, accessStatus, navigate, location]);

  // Prevent approved users from accessing the onboarding page
  useEffect(() => {
    if (isAuthenticated && !checkingAccess && accessStatus === "approved") {
      // Check if trying to access onboarding page
      if (isOnOnboardingPath()) {
        // Redirect to homepage or dashboard
        navigate('/');
      }
    }
  }, [isAuthenticated, checkingAccess, accessStatus, navigate, location]);

  // If loading or checking access or checking team members, don't render children yet
  if (isLoading || (isAuthenticated && (checkingAccess || checkingTeamMembers))) {
    return <ImavenLoadingScreen />
  }

  // Handle the different access states
  if (isAuthenticated) {
    switch (accessStatus) {
      case "approved":
        // User has approved access
        // Check if trying to access onboarding page
        if (isOnOnboardingPath()) {
          // Show loading while redirect happens in the useEffect
          return <ImavenLoadingScreen />;
        }
        
        // If team members exist but user hasn't authenticated as member yet
        // Show loading while redirecting to member login
        if (hasTeamMembers && !isMemberAuthenticated && !isOnMemberLoginPath()) {
          return <ImavenLoadingScreen />;
        }
        
        // Otherwise show the protected content
        return children;
      
      case "pending":
        // User's access is pending, show beautiful pending page
        return (
          <div className="access-pending-page" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #0a0c14 0%, #1a1f2e 50%, #0f1419 100%)',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Background decoration */}
            <div style={{
              position: 'absolute',
              top: '-100px',
              right: '-100px',
              width: '400px',
              height: '400px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)',
              filter: 'blur(60px)',
              pointerEvents: 'none'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-50px',
              left: '-50px',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, transparent 70%)',
              filter: 'blur(60px)',
              pointerEvents: 'none'
            }} />
            
            <div style={{
              maxWidth: '520px',
              padding: '48px 40px',
              borderRadius: '24px',
              background: 'linear-gradient(145deg, rgba(30, 35, 50, 0.9) 0%, rgba(20, 24, 36, 0.95) 100%)',
              border: '1px solid rgba(99, 102, 241, 0.15)',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.4), 0 0 80px rgba(99, 102, 241, 0.08)',
              position: 'relative',
              zIndex: 10
            }}>
              {/* Animated clock icon */}
              <div style={{ 
                marginBottom: '28px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid rgba(99, 102, 241, 0.3)',
                  boxShadow: '0 0 40px rgba(99, 102, 241, 0.2)'
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#8b5cf6" strokeWidth="2" />
                    <path d="M12 6V12L16 14" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
              
              <h1 style={{ 
                fontSize: '32px', 
                fontWeight: '700',
                marginBottom: '16px',
                background: 'linear-gradient(135deg, #ffffff 0%, #c7d2fe 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Application Under Review</h1>
              
              <p style={{ 
                fontSize: '17px', 
                lineHeight: '1.7', 
                color: '#94a3b8', 
                marginBottom: '12px' 
              }}>
                Thank you for joining <span style={{ color: '#a78bfa', fontWeight: '600' }}>iMaven AI</span>!
              </p>
              <p style={{ 
                fontSize: '16px', 
                lineHeight: '1.7', 
                color: '#64748b', 
                marginBottom: '32px' 
              }}>
                We're reviewing your application and will get back to you shortly. 
                You'll receive an email as soon as your account is approved.
              </p>
              
              {/* Progress indicator */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                marginBottom: '32px'
              }}>
                <div style={{
                  width: '40px',
                  height: '4px',
                  borderRadius: '2px',
                  background: 'linear-gradient(90deg, #6366f1, #8b5cf6)'
                }} />
                <div style={{
                  width: '40px',
                  height: '4px',
                  borderRadius: '2px',
                  background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)'
                }} />
                <div style={{
                  width: '40px',
                  height: '4px',
                  borderRadius: '2px',
                  background: 'rgba(99, 102, 241, 0.2)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.6), transparent)',
                    animation: 'shimmer 2s infinite'
                  }} />
                </div>
              </div>
              
              {/* Info box */}
              <div style={{
                padding: '20px',
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(99, 102, 241, 0.15)',
                marginBottom: '24px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '14px',
                  textAlign: 'left'
                }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: 'rgba(99, 102, 241, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#c7d2fe', fontWeight: '600', fontSize: '15px' }}>
                      Check your inbox
                    </p>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '14px', lineHeight: '1.5' }}>
                      We've sent a confirmation email. You'll be notified when approved.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Contact support */}
              <p style={{ 
                color: '#475569', 
                fontSize: '14px',
                margin: 0
              }}>
                Questions? Contact us at{' '}
                <a href="mailto:support@imavenai.com" style={{ 
                  color: '#8b5cf6', 
                  textDecoration: 'none',
                  fontWeight: '500'
                }}>
                  support@imavenai.com
                </a>
              </p>
            </div>
            
            {/* Add shimmer animation */}
            <style>{`
              @keyframes shimmer {
                0% { left: -100%; }
                100% { left: 100%; }
              }
            `}</style>
          </div>
        );
      
      case "not_started":
      case "not_registered":
        // Check if we're already on the onboarding path
        if (isOnOnboardingPath()) {
          // If already on onboarding path, display the page normally
          return children;
        }
        // Loading screen will show while redirecting (the useEffect above handles the redirection)
        return <ImavenLoadingScreen />;
      
      default:
        // Fallback for unexpected values
        return (
          <div className="access-error-page" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: '#1E1E1E',
            fontFamily: 'sans-serif'
          }}>
            <div style={{
              maxWidth: '500px',
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
              backgroundColor: '#2A2A2A'
            }}>
              <div style={{ marginBottom: '20px' }}>
                <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="11" stroke="#ef4444" strokeWidth="2" />
                  <path d="M12 8V12M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h1 style={{ 
                fontSize: '28px', 
                color: '#ef4444', 
                marginBottom: '20px',
                fontWeight: '600'
              }}>Access Error</h1>
              <p style={{ 
                fontSize: '18px', 
                lineHeight: '1.6', 
                color: '#e2e8f0', 
                marginBottom: '16px' 
              }}>
                An unexpected error occurred while checking your access status.
              </p>
              <p style={{ 
                fontSize: '16px', 
                lineHeight: '1.6', 
                color: '#a0aec0', 
                marginBottom: '20px' 
              }}>
                Please try again later or contact an administrator for assistance.
              </p>
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#471818',
                borderRadius: '8px',
                color: '#fca5a5',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                  <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#fca5a5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Contact: <a href="mailto:support@imavenai.com" style={{ color: '#fca5a5', textDecoration: 'underline', marginLeft: '4px' }}>support@imavenai.com</a>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
                <button
                  onClick={() => window.location.reload()}
                  style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #4b5563', background: 'transparent', color: '#e2e8f0', fontSize: '15px', cursor: 'pointer' }}
                >
                  Retry
                </button>
                <button
                  onClick={() => loginWithRedirect()}
                  style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#6366f1', color: '#fff', fontSize: '15px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Log in again
                </button>
              </div>
            </div>
          </div>
        );
    }
  }

  // User is not authenticated - show only the guest overlay
  // We don't render the actual dashboard children to avoid failed API calls
  // The overlay provides a clean landing page with sign up/login options
  return <GuestDashboardOverlay />;
};

export default ProtectedRoute;
